import type { AnyRow } from "@/lib/rowTypes";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertTriangle, CheckCircle, ChevronDown, ChevronRight,
  GraduationCap, ClipboardCheck, BookOpen, RefreshCw, Gamepad2,
  Calendar
} from "lucide-react";

type TabKey = "age_band" | "assessments" | "vocab_review" | "modules" | "examples";

interface LessonRefresh {
  lesson_id: string;
  lesson_name: string;
  course_name: string;
  has_simplified: boolean;
  has_standard: boolean;
  has_advanced: boolean;
  has_parent_prompt: boolean;
  has_parent_reinforcement: boolean;
  has_decision_11_13: boolean;
  has_decision_14_16: boolean;
  has_decision_17_plus: boolean;
  age_band_enabled: boolean;
  has_current_example: boolean;
  refresh_needed: boolean;
  example_date: string | null;
}

export default function RefinementCoveragePanel() {
  const [tab, setTab] = useState<TabKey>("age_band");
  const [lessonData, setLessonData] = useState<LessonRefresh[]>([]);
  const [assessmentBreakdown, setAssessmentBreakdown] = useState<Record<string, number>>({});
  const [moduleBreakdown, setModuleBreakdown] = useState<Record<string, number>>({});
  const [contrastCount, setContrastCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedCourse, setExpandedCourse] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      const [lessonsRes, assessRes, modRes, contrastRes] = await Promise.all([
        supabase
          .from("lessons")
          .select("lesson_id, lesson_name, course_name, simplified_explanation, standard_explanation, advanced_explanation, parent_discussion_prompt, parent_reinforcement_note, decision_prompt_11_13, decision_prompt_14_16, decision_prompt_17_plus, age_band_mode_enabled, current_example_title, current_example_body, example_date, refresh_needed")
          .order("course_name")
          .order("lesson_order"),
        supabase.from("assessments").select("id, assessment_type"),
        supabase.from("module_definitions").select("id, readiness"),
        supabase.from("vocab_contrast_pairs").select("id"),
      ]);

      const lessons = (lessonsRes.data || []).map((l: AnyRow) => ({
        lesson_id: l.lesson_id,
        lesson_name: l.lesson_name,
        course_name: l.course_name,
        has_simplified: !!l.simplified_explanation,
        has_standard: !!l.standard_explanation,
        has_advanced: !!l.advanced_explanation,
        has_parent_prompt: !!l.parent_discussion_prompt,
        has_parent_reinforcement: !!l.parent_reinforcement_note,
        has_decision_11_13: !!l.decision_prompt_11_13,
        has_decision_14_16: !!l.decision_prompt_14_16,
        has_decision_17_plus: !!l.decision_prompt_17_plus,
        age_band_enabled: !!l.age_band_mode_enabled,
        has_current_example: !!l.current_example_title && !!l.current_example_body,
        refresh_needed: !!l.refresh_needed,
        example_date: l.example_date,
      }));

      const assessGroups = ((assessRes.data) || []).reduce<Record<string, number>>((acc, a) => {
        const k = a.assessment_type || "other";
        acc[k] = (acc[k] || 0) + 1;
        return acc;
      }, {});
      const modGroups = ((modRes.data) || []).reduce<Record<string, number>>((acc, m) => {
        const k = m.readiness || "AnyRow";
        acc[k] = (acc[k] || 0) + 1;
        return acc;
      }, {});

      setLessonData(lessons);
      setAssessmentBreakdown(assessGroups);
      setModuleBreakdown(modGroups);
      setContrastCount((contrastRes.data || []).length);
      setLoading(false);
    };
    void load();
  }, []);

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card/60 p-6">
        <p className="text-sm text-muted-foreground animate-pulse">Loading refinement coverage…</p>
      </div>
    );
  }

  const totalLessons = lessonData.length;

  const TABS: { key: TabKey; label: string; icon: typeof GraduationCap }[] = [
    { key: "age_band", label: "Age Band", icon: GraduationCap },
    { key: "assessments", label: "Assessments", icon: ClipboardCheck },
    { key: "vocab_review", label: "Vocab Review", icon: BookOpen },
    { key: "modules", label: "Modules", icon: Gamepad2 },
    { key: "examples", label: "Examples", icon: RefreshCw },
  ];

  const ageBandStats = {
    simplified: lessonData.filter(l => l.has_simplified).length,
    standard: lessonData.filter(l => l.has_standard).length,
    advanced: lessonData.filter(l => l.has_advanced).length,
    parent: lessonData.filter(l => l.has_parent_prompt).length,
    reinforcement: lessonData.filter(l => l.has_parent_reinforcement).length,
    decision11: lessonData.filter(l => l.has_decision_11_13).length,
    decision14: lessonData.filter(l => l.has_decision_14_16).length,
    decision17: lessonData.filter(l => l.has_decision_17_plus).length,
    enabled: lessonData.filter(l => l.age_band_enabled).length,
  };

  const exampleStats = {
    hasExample: lessonData.filter(l => l.has_current_example).length,
    needRefresh: lessonData.filter(l => l.refresh_needed).length,
  };

  // Group by course
  const courseMap = new Map<string, LessonRefresh[]>();
  lessonData.forEach(l => {
    const arr = courseMap.get(l.course_name) || [];
    arr.push(l);
    courseMap.set(l.course_name, arr);
  });

  return (
    <div className="space-y-4">
      {/* Tab selector */}
      <div className="flex gap-1 flex-wrap">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-heading font-medium transition-colors ${
                tab === t.key
                  ? "bg-primary/15 text-primary border border-primary/30"
                  : "bg-muted/30 text-muted-foreground hover:text-foreground border border-transparent"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Age Band Tab */}
      {tab === "age_band" && (
        <div className="space-y-3">
          <div className="rounded-xl border border-border bg-card/60 p-4">
            <h3 className="font-heading text-sm font-semibold text-foreground mb-3">Age-Band Content Coverage</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[
                { label: "Simplified (11–13)", count: ageBandStats.simplified, color: "text-cyan-400" },
                { label: "Standard (14–16)", count: ageBandStats.standard, color: "text-blue-400" },
                { label: "Advanced (17+)", count: ageBandStats.advanced, color: "text-purple-400" },
                { label: "Parent Discussion", count: ageBandStats.parent, color: "text-amber-400" },
                { label: "Parent Reinforcement", count: ageBandStats.reinforcement, color: "text-amber-400" },
                { label: "Decision 11–13", count: ageBandStats.decision11, color: "text-pink-400" },
                { label: "Decision 14–16", count: ageBandStats.decision14, color: "text-pink-400" },
                { label: "Decision 17+", count: ageBandStats.decision17, color: "text-pink-400" },
                { label: "Band Enabled", count: ageBandStats.enabled, color: "text-emerald-400" },
              ].map(s => (
                <div key={s.label} className="rounded-lg border border-border/50 bg-background/40 p-2.5">
                  <p className="text-[10px] font-heading text-muted-foreground">{s.label}</p>
                  <p className="text-xs font-heading font-bold text-foreground">
                    {s.count}/{totalLessons}{" "}
                    <span className={s.count === totalLessons ? "text-emerald-400" : s.count > 0 ? "text-amber-400" : "text-destructive"}>
                      ({Math.round((s.count / totalLessons) * 100)}%)
                    </span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Assessments Tab */}
      {tab === "assessments" && (
        <div className="rounded-xl border border-border bg-card/60 p-4 space-y-3">
          <h3 className="font-heading text-sm font-semibold text-foreground">Assessment Coverage</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="rounded-lg border border-border/50 bg-background/40 p-2.5">
              <p className="text-[10px] font-heading text-muted-foreground">Total</p>
              <p className="text-lg font-heading font-bold text-foreground">
                {Object.values(assessmentBreakdown).reduce((s, n) => s + n, 0)}
              </p>
            </div>
            {Object.entries(assessmentBreakdown).map(([type, count]) => (
              <div key={type} className="rounded-lg border border-border/50 bg-background/40 p-2.5">
                <p className="text-[10px] font-heading text-muted-foreground capitalize">{type.replace(/_/g, " ")}</p>
                <p className="text-lg font-heading font-bold text-foreground">{count}</p>
              </div>
            ))}
          </div>
          {Object.keys(assessmentBreakdown).length === 0 && (
            <p className="text-xs text-muted-foreground">No assessments registered yet.</p>
          )}
        </div>
      )}

      {/* Vocab Review Tab */}
      {tab === "vocab_review" && (
        <div className="rounded-xl border border-border bg-card/60 p-4 space-y-3">
          <h3 className="font-heading text-sm font-semibold text-foreground">Vocabulary Reinforcement</h3>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg border border-border/50 bg-background/40 p-2.5">
              <p className="text-[10px] font-heading text-muted-foreground">Contrast Pairs</p>
              <p className="text-lg font-heading font-bold text-foreground">{contrastCount}</p>
            </div>
            <div className="rounded-lg border border-border/50 bg-background/40 p-2.5">
              <p className="text-[10px] font-heading text-muted-foreground">Spaced Review</p>
              <p className="text-lg font-heading font-bold text-foreground">Active</p>
            </div>
          </div>
        </div>
      )}

      {/* Modules Tab */}
      {tab === "modules" && (
        <div className="rounded-xl border border-border bg-card/60 p-4 space-y-3">
          <h3 className="font-heading text-sm font-semibold text-foreground">Module / Simulator Registry</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="rounded-lg border border-border/50 bg-background/40 p-2.5">
              <p className="text-[10px] font-heading text-muted-foreground">Total Registered</p>
              <p className="text-lg font-heading font-bold text-foreground">
                {Object.values(moduleBreakdown).reduce((s, n) => s + n, 0)}
              </p>
            </div>
            {Object.entries(moduleBreakdown).map(([readiness, count]) => (
              <div key={readiness} className="rounded-lg border border-border/50 bg-background/40 p-2.5">
                <p className="text-[10px] font-heading text-muted-foreground capitalize">{readiness.replace(/_/g, " ")}</p>
                <p className={`text-lg font-heading font-bold ${readiness === "live" ? "text-emerald-400" : readiness === "coming_soon" ? "text-amber-400" : "text-foreground"}`}>{count}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Examples Tab */}
      {tab === "examples" && (
        <div className="space-y-3">
          <div className="rounded-xl border border-border bg-card/60 p-4">
            <h3 className="font-heading text-sm font-semibold text-foreground mb-3">Current Example Freshness</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <div className="rounded-lg border border-border/50 bg-background/40 p-2.5">
                <p className="text-[10px] font-heading text-muted-foreground">Has Example</p>
                <p className="text-xs font-heading font-bold text-foreground">
                  {exampleStats.hasExample}/{totalLessons}
                </p>
              </div>
              <div className="rounded-lg border border-border/50 bg-background/40 p-2.5">
                <p className="text-[10px] font-heading text-muted-foreground">Needs Refresh</p>
                <p className={`text-xs font-heading font-bold ${exampleStats.needRefresh > 0 ? "text-amber-400" : "text-emerald-400"}`}>
                  {exampleStats.needRefresh}
                </p>
              </div>
            </div>
          </div>

          {/* Course-level breakdown for refresh-needed */}
          {exampleStats.needRefresh > 0 && (
            <div className="space-y-1">
              {Array.from(courseMap.entries()).map(([course, lessons]) => {
                const needRefresh = lessons.filter(l => l.refresh_needed);
                if (needRefresh.length === 0) return null;
                const isExpanded = expandedCourse === course;
                return (
                  <div key={course} className="rounded-xl border border-border/60 bg-card/40 overflow-hidden">
                    <button
                      onClick={() => setExpandedCourse(isExpanded ? null : course)}
                      className="w-full flex items-center gap-2 p-3 text-left hover:bg-muted/30 transition-colors"
                    >
                      <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
                      <span className="text-xs font-heading font-semibold text-foreground flex-1 truncate">{course}</span>
                      <span className="text-[10px] font-body text-amber-400">{needRefresh.length} stale</span>
                      {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                    </button>
                    {isExpanded && (
                      <div className="border-t border-border/40 p-2 space-y-1">
                        {needRefresh.map(l => (
                          <div key={l.lesson_id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted/20">
                            <RefreshCw className="h-3 w-3 text-amber-400" />
                            <span className="text-[11px] font-body text-foreground/80 flex-1 truncate">{l.lesson_name}</span>
                            {l.example_date && (
                              <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {new Date(l.example_date).toLocaleDateString("en-US", { month: "short", year: "2-digit" })}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
